import { useEffect, useRef, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { ColumnConfigEntry, ColumnSorting } from '@erp/shared-types';
import {
  addNewDefaults,
  removeColumnFrom,
  resolveColumns,
  type SavedColumnsMode,
} from './columnConfigMerge';
import {
  useUserTableColumnConfig,
  useUpsertUserTableColumnConfig,
} from '@/api/rpc/hooks/core/useUserTableColumnConfig';
import { useOrgTableColumnConfig } from '@/api/rpc/hooks/core/useOrgTableColumnConfig';

/** Re-export the shared type under its original name for backwards compatibility */
export type CustomColumnConfig = ColumnConfigEntry;

/** Whose layout a table is showing: the user's own, the org default, or the built-in one. */
export type ColumnViewSource = 'user' | 'org' | 'default';

const NO_SORTING: ColumnSorting = [];

/**
 * The sort a table shows: the user's saved sort, else the org default's, else
 * the built-in default. A layout that never saved a sort inherits the next one.
 */
export function resolveSorting({
  user,
  org,
  defaults,
}: {
  user: ColumnSorting | undefined;
  org: ColumnSorting | undefined;
  defaults: ColumnSorting;
}): ColumnSorting {
  return user ?? org ?? defaults;
}

export function columnViewSource({
  user,
  org,
}: {
  user: { columns: ColumnConfigEntry[]; sorting?: ColumnSorting } | null;
  org: { columns: ColumnConfigEntry[]; sorting?: ColumnSorting } | null;
}): ColumnViewSource {
  if (user && (user.columns.length > 0 || user.sorting !== undefined)) {
    return 'user';
  }
  if (org && (org.columns.length > 0 || org.sorting !== undefined)) {
    return 'org';
  }
  return 'default';
}

/**
 * The latest `columns`, but keeping the previous array while its content is
 * unchanged — so callers may pass inline arrays without re-deriving every
 * render, and changed defaults (e.g. a refetched task page) still apply.
 */
function useContentStableColumns(
  columns: CustomColumnConfig[],
): CustomColumnConfig[] {
  const key = JSON.stringify(columns);
  const [stable, setStable] = useState({ key, columns });
  if (stable.key !== key) {
    setStable({ key, columns });
    return columns;
  }
  return stable.columns;
}

/**
 * Generic hook for persisting user-configured columns per table.
 * Backed by server-side storage so configs follow the user across devices.
 *
 * @param tableId - Stable identifier for the table (e.g. "ship-and-debit").
 * @param defaultColumns - Company-default column set shown when the user has no saved config.
 * @param options.savedColumns - How a saved config combines with the
 *   defaults; see {@link SavedColumnsMode}. Default `legacy`.
 * @param options.defaultSorting - Sort shown when neither the user nor the
 *   org saved one. Default: unsorted.
 */
export function useColumnConfig(
  tableId: string,
  defaultColumns: CustomColumnConfig[] = [],
  {
    savedColumns = 'legacy',
    defaultSorting = NO_SORTING,
  }: { savedColumns?: SavedColumnsMode; defaultSorting?: ColumnSorting } = {},
) {
  // `isPending`, not `isLoading`: a fetch cancelled on remount leaves the
  // query pending but idle, which `isLoading` reports as loaded.
  const { data, isPending: isUserLoading } = useUserTableColumnConfig(tableId);
  const { data: orgData, isPending: isOrgLoading } =
    useOrgTableColumnConfig(tableId);
  const { mutate: upsert } = useUpsertUserTableColumnConfig();
  const migrationAttempted = useRef(false);
  const stableDefaults = useContentStableColumns(defaultColumns);

  const isLoading = isUserLoading || isOrgLoading;

  // Effective defaults: org config if available, otherwise hardcoded defaults.
  // An org layout saved before a built-in column existed gets that column
  // too, under the same rule a user's saved layout does.
  const orgColumns = orgData?.columns ?? [];
  const effectiveDefaults =
    orgColumns.length === 0
      ? stableDefaults
      : savedColumns === 'addNewDefaults'
        ? addNewDefaults(orgColumns, stableDefaults)
        : orgColumns;

  // Migrate localStorage columns to DB on first load
  useEffect(() => {
    if (isLoading || migrationAttempted.current) return;
    migrationAttempted.current = true;
    if (data?.columns && data.columns.length > 0) return; // DB already has columns

    const lsKey = `table-custom-columns:${tableId}`;
    const raw = localStorage.getItem(lsKey);
    if (!raw) return;

    try {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) return;

      // Basic shape validation before merging
      const isValid = parsed.every(
        (item: unknown) =>
          typeof item === 'object' &&
          item !== null &&
          'id' in item &&
          typeof (item as Record<string, unknown>).id === 'string' &&
          'header' in item &&
          typeof (item as Record<string, unknown>).header === 'string',
      );
      if (!isValid) return;

      const validated = parsed as CustomColumnConfig[];
      const merged = [...stableDefaults, ...validated];
      upsert({ tableId, columns: merged });
      localStorage.removeItem(lsKey);
    } catch {
      // Corrupt localStorage entry — ignore
    }
  }, [data, isLoading, stableDefaults, tableId, upsert]);

  const columns = resolveColumns({
    stored: data?.columns ?? [],
    defaults: effectiveDefaults,
    isLoading,
    mode: savedColumns,
  });

  const sorting = resolveSorting({
    user: data?.sorting,
    org: orgData?.sorting,
    defaults: defaultSorting,
  });
  const viewSource = columnViewSource({
    user: data ?? null,
    org: orgData ?? null,
  });

  function persist(cols: CustomColumnConfig[]) {
    upsert({ tableId, columns: cols });
  }

  function replaceColumns(cols: CustomColumnConfig[]) {
    persist(cols);
  }

  /**
   * Save a sort with the user's view. Their saved columns go along unchanged
   * (empty while they still follow the defaults), so sorting alone never pins
   * the column layout.
   */
  function setSorting(next: ColumnSorting) {
    upsert({ tableId, columns: data?.columns ?? [], sorting: next });
  }

  /** Drop the user's own view so the table follows the org default again. */
  function clearUserView() {
    upsert({ tableId, columns: [], sorting: null });
  }

  function addColumn(config: Omit<CustomColumnConfig, 'id'>) {
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    persist([...columns, { ...config, id }]);
  }

  function removeColumn(id: string) {
    persist(removeColumnFrom(columns, id, effectiveDefaults, savedColumns));
  }

  function updateColumn(
    id: string,
    updates: Partial<Omit<CustomColumnConfig, 'id'>>,
  ) {
    persist(
      columns.map((col) => (col.id === id ? { ...col, ...updates } : col)),
    );
  }

  function reorderColumns(fromIndex: number, toIndex: number) {
    persist(arrayMove(columns, fromIndex, toIndex));
  }

  function resetToDefaults() {
    persist(effectiveDefaults);
  }

  return {
    columns,
    sorting,
    viewSource,
    addColumn,
    removeColumn,
    updateColumn,
    reorderColumns,
    replaceColumns,
    setSorting,
    resetToDefaults,
    clearUserView,
    isLoading,
  };
}
